/**
 * InnerScrollableGalleryV2
 *
 * Autoplay is a compositor Web Animation over one setWidth. Duration is speed
 * (one content set per N seconds), not a CSS iteration. Copies fill the
 * bleed viewport plus one set on each side; x is always folded into
 * [-2 * setWidth, -setWidth) so copies cover both the left bleed and
 * the right edge of the viewport. Drag cancels the animation, writes the
 * same wrap band, then resumes from the current x.
 *
 * Pad/MO caption frost stays live only near the viewport. Far clones get
 * `.isgv2-frost-far`. Preheat is 2 * setWidth + viewport on each side so
 * wrapX jumps and coalesced drags never flash a sharp caption.
 */
const ISGV2_FROST_PX = 48;

class InnerScrollableGalleryV2 extends HTMLElement {
  connectedCallback() {
    if (this.dataset.isgv2Mounted === 'true') return;

    this.source = this.querySelector('[data-isgv2-source]');
    this.viewport = this.querySelector('[data-isgv2-viewport]');
    this.scroller = this.querySelector('[data-isgv2-scroller]');
    this.track = this.querySelector('[data-isgv2-track]');

    if (!this.source || !this.viewport || !this.scroller || !this.track) return;

    this.hoverFactor = Number(this.dataset.isgv2HoverFactor || 0.4);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.pcQuery = window.matchMedia('(min-width: 1280px)');

    this.isLooping = false;
    this.isDragging = false;
    this.isHovered = false;
    this.isInView = true;
    this.isBuilding = false;
    this.setWidth = 0;
    this.setCount = 0;
    this.x = 0;
    this.dragStartX = 0;
    this.dragStartOffsetX = 0;
    this.activePointerId = null;
    this.frostRaf = 0;
    this.layoutRaf = 0;
    this.lastFrostX = 0;
    this.animation = null;
    this.frostRanges = [];
    this.viewportPadStart = 0;
    this.viewportWidth = 0;

    this.onPointerDown = this.handlePointerDown.bind(this);
    this.onPcQueryChange = this.handlePcQueryChange.bind(this);
    this.onPointerMove = this.handlePointerMove.bind(this);
    this.onPointerUp = this.handlePointerUp.bind(this);
    this.onHoverStart = this.handleHoverStart.bind(this);
    this.onHoverEnd = this.handleHoverEnd.bind(this);
    this.onFrostTick = this.tickFrost.bind(this);

    this.viewport.addEventListener('pointerdown', this.onPointerDown);
    this.viewport.addEventListener('pointerenter', this.onHoverStart);
    this.viewport.addEventListener('pointerleave', this.onHoverEnd);
    this.pcQuery.addEventListener('change', this.onPcQueryChange);

    this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
    this.resizeObserver.observe(this.viewport);

    if ('IntersectionObserver' in window) {
      this.viewObserver = new IntersectionObserver((entries) => {
        this.isInView = entries.some((entry) => entry.isIntersecting);
        if (this.isInView) this.startLoop();
        else this.stopLoop();
      });
      this.viewObserver.observe(this.viewport);
    }

    this.buildTrack();
    this.dataset.isgv2Mounted = 'true';
  }

  disconnectedCallback() {
    this.stopLoop();
    if (this.layoutRaf) cancelAnimationFrame(this.layoutRaf);
    this.layoutRaf = 0;
    this.unbindDragWindow();
    this.resizeObserver?.disconnect();
    this.viewObserver?.disconnect();
    this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
    this.viewport?.removeEventListener('pointerenter', this.onHoverStart);
    this.viewport?.removeEventListener('pointerleave', this.onHoverEnd);
    this.pcQuery?.removeEventListener('change', this.onPcQueryChange);
    this.returnItemsToSource();
    this.frostRanges = [];
    this.resizeObserver = null;
    this.viewObserver = null;
    this.dataset.isgv2Mounted = 'false';
  }

  bindDragWindow() {
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
  }

  unbindDragWindow() {
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
  }

  /** @returns {boolean} */
  isPc() {
    return this.pcQuery.matches;
  }

  handleHoverStart() {
    this.isHovered = true;
    this.syncPlaybackRate();
  }

  handleHoverEnd() {
    this.isHovered = false;
    this.syncPlaybackRate();
  }

  handlePcQueryChange() {
    if (!this.isPc()) {
      this.cacheFrostMetrics();
      if (!this.frostRanges.length) this.cacheFrostRanges();
    }
    this.syncFrost();
  }

  /**
   * @param {number} setWidth
   * @param {number} viewportWidth
   * @returns {number}
   */
  getCopyCount(setWidth, viewportWidth) {
    if (setWidth <= 0) return 3;
    return Math.max(3, Math.ceil(viewportWidth / setWidth) + 2);
  }

  /**
   * Fold x into [-2 * setWidth, -setWidth). That band always has a full set
   * to the left of the content well (bleed) and enough copies to the right.
   * @param {number} x
   * @returns {number}
   */
  wrapX(x) {
    const width = this.setWidth;
    if (width <= 0) return x;
    const min = -2 * width;
    let unit = (x - min) % width;
    if (unit < 0) unit += width;
    return min + unit;
  }

  /**
   * One content set in milliseconds.
   * @returns {number}
   */
  getDurationMs() {
    const duration = Number(this.dataset.isgv2Duration);
    const seconds = Number.isFinite(duration) && duration > 0 ? duration : 50;
    return seconds * 1000;
  }

  applyX() {
    if (!this.scroller) return;
    if (this.setWidth > 0) this.x = this.wrapX(this.x);
    this.scroller.style.transform = `translate3d(${this.x}px, 0, 0)`;
    this.syncFrost();
  }

  cacheFrostMetrics() {
    if (!this.viewport) return;
    this.viewportWidth = this.viewport.clientWidth || 0;
    this.viewportPadStart = parseFloat(getComputedStyle(this.viewport).paddingInlineStart) || 0;
  }

  /**
   * Cache each item’s range in track coordinates. Both rects include the
   * scroller transform, so the difference is independent of x.
   */
  cacheFrostRanges() {
    this.frostRanges = [];
    if (!this.track) return;

    const trackRect = this.track.getBoundingClientRect();
    if (trackRect.width <= 0) return;

    this.track.querySelectorAll('[data-isgv2-item]').forEach((el) => {
      const rect = el.getBoundingClientRect();
      const left = rect.left - trackRect.left;
      this.frostRanges.push({
        el,
        left,
        right: left + rect.width,
        far: el.classList.contains('isgv2-frost-far'),
      });
    });
  }

  /**
   * Pad/MO: drop live blur on clones outside the preheat band.
   * PC captions stay hover-only (CSS); strip far-state so resize is clean.
   */
  syncFrost() {
    if (!this.frostRanges.length) return;

    if (this.isPc()) {
      this.frostRanges.forEach((range) => {
        if (!range.far) return;
        range.el.classList.remove('isgv2-frost-far');
        range.far = false;
      });
      return;
    }

    const setWidth = this.setWidth;
    const visLeft = -this.viewportPadStart - this.x;
    const visRight = this.viewportWidth - this.viewportPadStart - this.x;
    const preheat =
      setWidth > 0 ? setWidth * 2 + this.viewportWidth : this.viewportWidth;
    const activeLeft = visLeft - preheat;
    const activeRight = visRight + preheat;

    this.frostRanges.forEach((range) => {
      const near = range.right > activeLeft && range.left < activeRight;
      const far = !near;
      if (far === range.far) return;
      range.el.classList.toggle('isgv2-frost-far', far);
      range.far = far;
    });
  }

  /** @returns {boolean} */
  canRunLoop() {
    return this.isLooping && this.isInView && this.setWidth > 0 && this.setCount >= 3;
  }

  startLoop() {
    if (!this.canRunLoop() || this.isDragging) return;
    this.startAutoplay();
  }

  stopLoop() {
    this.stopAutoplay();
  }

  /**
   * Read compositor x for frost only. Never write transform while WAAPI owns it.
   */
  tickFrost() {
    if (!this.animation || !this.canRunLoop()) {
      this.frostRaf = 0;
      return;
    }

    this.frostRaf = requestAnimationFrame(this.onFrostTick);

    if (this.isPc() || this.isDragging || !this.scroller) return;

    const x = this.readTransformX();
    if (x == null) return;
    this.x = x;
    if (Math.abs(x - this.lastFrostX) < ISGV2_FROST_PX) return;
    this.lastFrostX = x;
    this.syncFrost();
  }

  /** @returns {number | null} */
  readTransformX() {
    if (!this.scroller) return null;
    const transform = getComputedStyle(this.scroller).transform;
    if (!transform || transform === 'none') return this.x;
    try {
      return new DOMMatrixReadOnly(transform).m41;
    } catch {
      return this.x;
    }
  }

  /**
   * Autoplay stays inside the wrap band:
   * left  [-setWidth, -2 * setWidth], right [-2 * setWidth, -setWidth].
   * After drag, a short tail animation reaches the seam, then the infinite
   * cycle jumps to the visually identical wrap point.
   */
  startAutoplay() {
    if (!this.canRunLoop() || this.isDragging || !this.scroller) return;
    if (typeof this.scroller.animate !== 'function') return;
    if (this.animation && this.animation.playState === 'running') return;

    this.stopAutoplay();

    const width = this.setWidth;
    const duration = this.getDurationMs();
    const isRight = this.dataset.isgv2Direction === 'right';
    const loopStart = isRight ? -2 * width : -width;
    const loopEnd = isRight ? -width : -2 * width;

    this.x = this.wrapX(this.x);
    const start = this.x;
    const remaining = isRight ? loopEnd - start : start - loopEnd;

    const runInfinite = () => {
      this.x = loopStart;
      this.animation = this.playAutoplay(loopStart, loopEnd, {
        duration,
        easing: 'linear',
        iterations: Infinity,
      });
      this.syncPlaybackRate();
      this.startFrostRaf();
    };

    if (remaining < 0.5 || Math.abs(remaining - width) < 0.5) {
      runInfinite();
      return;
    }

    const tailDuration = Math.max(16, duration * (remaining / width));
    this.animation = this.playAutoplay(start, loopEnd, {
      duration: tailDuration,
      easing: 'linear',
      iterations: 1,
      fill: 'forwards',
    });
    this.syncPlaybackRate();
    this.animation.onfinish = () => {
      if (this.animation) {
        this.animation.onfinish = null;
        try {
          this.animation.commitStyles();
        } catch {
          /* noop */
        }
        this.animation.cancel();
        this.animation = null;
      }
      runInfinite();
    };
    this.startFrostRaf();
  }

  /**
   * Inline transform beats WAAPI. Clear it after creating the animation
   * so the compositor owns autoplay.
   * @param {number} fromX
   * @param {number} toX
   * @param {KeyframeAnimationOptions} options
   * @returns {Animation}
   */
  playAutoplay(fromX, toX, options) {
    this.scroller.style.transform = `translate3d(${fromX}px, 0, 0)`;
    const animation = this.scroller.animate(
      [
        { transform: `translate3d(${fromX}px, 0, 0)` },
        { transform: `translate3d(${toX}px, 0, 0)` },
      ],
      options
    );
    const clearInline = () => {
      if (this.animation === animation && this.scroller) {
        this.scroller.style.transform = '';
      }
    };
    if (animation.ready) animation.ready.then(clearInline);
    else clearInline();
    return animation;
  }

  stopAutoplay() {
    if (this.frostRaf) cancelAnimationFrame(this.frostRaf);
    this.frostRaf = 0;

    if (!this.animation) return;

    try {
      this.animation.commitStyles();
    } catch {
      /* noop */
    }

    const liveX = this.readTransformX();
    this.animation.onfinish = null;
    this.animation.cancel();
    this.animation = null;

    if (liveX != null) this.x = liveX;
    if (this.setWidth > 0) this.x = this.wrapX(this.x);
    if (this.scroller) {
      this.scroller.style.transform = `translate3d(${this.x}px, 0, 0)`;
    }
  }

  startFrostRaf() {
    if (this.frostRaf || this.isPc()) return;
    this.frostRaf = requestAnimationFrame(this.onFrostTick);
  }

  syncPlaybackRate() {
    if (!this.animation) return;
    let rate = 1;
    if (this.isPc() && this.isHovered) {
      const factor = Number.isFinite(this.hoverFactor) ? this.hoverFactor : 0.4;
      rate = Math.min(1, Math.max(0.1, factor));
    }
    this.animation.playbackRate = rate;
  }

  scheduleLayout() {
    if (this.layoutRaf || this.isBuilding) return;
    this.layoutRaf = requestAnimationFrame(() => {
      this.layoutRaf = 0;
      this.syncLayout();
    });
  }

  syncLayout() {
    if (!this.isLooping || this.isBuilding) return;

    const setWidth = this.measureSetWidth();
    if (setWidth <= 0) return;

    const viewportWidth = this.viewport?.clientWidth || 0;
    const copies = this.getCopyCount(setWidth, viewportWidth);

    if (setWidth > 0 && copies !== this.setCount) {
      this.buildTrack();
      return;
    }

    this.cacheFrostMetrics();

    if (setWidth > 0 && setWidth !== this.setWidth) {
      this.setWidth = setWidth;
      this.cacheFrostRanges();
      this.stopAutoplay();
      this.startLoop();
      return;
    }

    this.syncFrost();
  }

  buildTrack() {
    this.stopLoop();
    this.returnItemsToSource();
    this.track.replaceChildren();
    this.viewport.classList.remove('isgv2-bleed');
    this.track.classList.remove('isgv2-static');
    this.scroller.classList.remove('isgv2-looping');
    this.scroller.style.transform = '';
    this.setWidth = 0;
    this.setCount = 0;
    this.x = 0;
    this.lastFrostX = 0;
    this.isLooping = false;
    this.frostRanges = [];

    const items = Array.from(this.source.querySelectorAll('[data-isgv2-item]'));

    if (items.length === 0) {
      this.track.classList.add('isgv2-static');
      this.stopLoop();
      return;
    }

    this.track.appendChild(this.createSet(items, true));

    if (this.reducedMotion) {
      this.track.classList.add('isgv2-static');
      this.setWidth = this.measureSetWidth();
      this.cacheFrostMetrics();
      this.cacheFrostRanges();
      this.syncFrost();
      this.stopLoop();
      return;
    }

    this.isBuilding = true;
    this.viewport.classList.add('isgv2-bleed');
    this.setWidth = this.measureSetWidth();

    if (this.setWidth <= 0) {
      this.isLooping = true;
      this.setCount = 1;
      this.isBuilding = false;
      this.scheduleLayout();
      return;
    }

    const copies = this.getCopyCount(this.setWidth, this.viewport.clientWidth);

    for (let index = 1; index < copies; index += 1) {
      this.track.appendChild(this.createSet(items, false));
    }

    this.setCount = copies;
    this.isLooping = true;
    this.scroller.classList.add('isgv2-looping');
    this.cacheFrostMetrics();
    this.cacheFrostRanges();
    this.x = this.wrapX(-this.setWidth);
    this.applyX();
    this.isBuilding = false;
    this.startLoop();
  }

  /**
   * @param {Element[]} items
   * @param {boolean} adoptOriginals
   * @returns {HTMLElement}
   */
  createSet(items, adoptOriginals) {
    const set = document.createElement('div');
    set.className = 'isgv2__set';

    if (!adoptOriginals) {
      set.setAttribute('aria-hidden', 'true');
    }

    items.forEach((item) => {
      if (adoptOriginals) {
        set.appendChild(item);
      } else {
        set.appendChild(this.cloneItem(item));
      }
    });

    return set;
  }

  /**
   * @param {Element} item
   * @returns {Element}
   */
  cloneItem(item) {
    const clone = item.cloneNode(true);
    clone.removeAttribute('id');
    clone.setAttribute('data-isgv2-clone', '');
    clone.classList.remove('isgv2-frost-far');
    clone.setAttribute('aria-hidden', 'true');
    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    return clone;
  }

  returnItemsToSource() {
    if (!this.source || !this.track) return;

    Array.from(this.track.querySelectorAll('[data-isgv2-item]:not([data-isgv2-clone])')).forEach(
      (item) => {
        this.source.appendChild(item);
        item.classList.remove('isgv2-frost-far');
      }
    );
  }

  measureSetWidth() {
    const firstSet = this.track?.querySelector('.isgv2__set');
    return firstSet?.offsetWidth || 0;
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerDown(event) {
    if (!this.isLooping || this.setWidth <= 0) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.stopAutoplay();
    this.applyX();
    this.isDragging = true;
    this.activePointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartOffsetX = this.x;
    this.viewport.classList.add('isgv2-dragging');
    this.bindDragWindow();

    if (event.pointerType === 'mouse') {
      event.preventDefault();
    }

    try {
      this.viewport.setPointerCapture(event.pointerId);
    } catch {
      /* noop */
    }
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerMove(event) {
    if (!this.isDragging) return;
    if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;

    this.x = this.dragStartOffsetX + (event.clientX - this.dragStartX);
    this.applyX();
  }

  /**
   * @param {PointerEvent} event
   */
  handlePointerUp(event) {
    if (!this.isDragging) return;
    if (this.activePointerId != null && event.pointerId !== this.activePointerId) return;

    this.isDragging = false;
    this.activePointerId = null;
    this.applyX();
    this.viewport.classList.remove('isgv2-dragging');
    this.unbindDragWindow();

    try {
      this.viewport.releasePointerCapture(event.pointerId);
    } catch {
      /* noop */
    }

    if (this.isInView) this.startAutoplay();
  }
}

if (!customElements.get('inner-scrollable-gallery-v2')) {
  customElements.define('inner-scrollable-gallery-v2', InnerScrollableGalleryV2);
}
